import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import {
  setInitialDataForPdf,
  updateActiveDocument,
} from "../store/documentSlice";
import { Document } from "../";
import { setMetadataLanguage } from "../../metadata/store/metadataSlice";
import { Status } from "../../common/status/status";
import camelcaseKeys from "camelcase-keys";
import { useLazyGetDocumentQuery } from "../store/documentApi";
import {
  removeAnnotatorError,
  setAnnotatorError,
} from "../../annotator/store/AnnotatorSlice";
import { isImageDocument, isPdfDocument } from "../utils/mimeType";
import { AnnotatorErrorType } from "../../annotator/interfaces/errorType";
import { shallowEqual } from "react-redux";
import { clearGeometryCache } from "../../annotation/selectors/tokensGeometrySelector";

const useActiveDocument = () => {
  const dispatch = useAppDispatch();

  const { activeDocument: activeDocumentMinimal } = useAppSelector(
    (state) => state.documentReducer,
    shallowEqual,
  );
  const { activeDocumentSet } = useAppSelector(
    (state) => state.documentSetsReducer,
    shallowEqual,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const setActiveDocument = useCallback(
    (document?: Document) => {
      dispatch(updateActiveDocument(document));
    },
    [dispatch],
  );

  const activeDocument = useMemo(() => {
    if (!activeDocumentSet?.documents?.length || !activeDocumentMinimal) {
      return undefined;
    }

    return activeDocumentSet.documents.find(
      (d) => d.id === activeDocumentMinimal.id,
    );
  }, [activeDocumentMinimal, activeDocumentSet]);

  const [getDocument] = useLazyGetDocumentQuery();

  const fetchDocument = useCallback(
    async (documentId: string, status: Status, signal?: AbortSignal) => {
      if (activeDocumentMinimal?.id === documentId) {
        return;
      }

      try {
        setIsLoading(true);

        const data = await getDocument(documentId).unwrap();
        const { metaData, documentType, pageCount } = data;

        dispatch(
          updateActiveDocument({
            ...data,
            metaData: camelcaseKeys(metaData),
            documentType: documentType,
          }),
        );

        // Whatever the annotator can render page by page: a PDF, or an image, which is
        // a single page. Falls back to the filename when a document carries no mime
        // type, so this is the same condition it always was for those.
        if (
          (isPdfDocument(data) || isImageDocument(data)) &&
          !activeDocumentSet!.input.legacyAnnotator
        ) {
          clearGeometryCache();
          dispatch(
            setInitialDataForPdf({
              pageCount,
            }),
          );
        }

        dispatch(setMetadataLanguage(metaData?.language || "en"));
        dispatch(
          removeAnnotatorError(AnnotatorErrorType.DOCUMENT_DATA_LOAD_ERROR),
        );
      } catch (e: unknown) {
        if (signal?.aborted) {
          return;
        }

        dispatch(
          setAnnotatorError(AnnotatorErrorType.DOCUMENT_DATA_LOAD_ERROR),
        );

        console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [activeDocumentMinimal?.id, dispatch, getDocument, activeDocumentSet],
  );

  return {
    activeDocument,
    activeDocumentMinimal,
    setActiveDocument,
    fetchDocument,
    isLoading,
  };
};

export default useActiveDocument;
